import {
  ApiV2Error,
  type MaybeFunction,
  type MaybePromise,
  type UnwrapMaybeFunction,
} from '@combinamas/client-v2'
import type { App } from '@combinamas/server'
import type { Treaty } from '@elysiajs/eden'
import { treaty } from '@elysiajs/eden'
import { redirect } from '@tanstack/react-router'
import { createIsomorphicFn } from '@tanstack/react-start'
import { getCookie as getClientCookie } from '@/utils/cookies-client'

export const getApi = createIsomorphicFn()
  .server(() => {
    const serverUrl = `http://localhost:${process.env.PORT || 8004}`
    return treaty<App>(serverUrl, {
      fetch: {
        cache: 'no-store',
      },
      parseDate: false,
      onRequest: async () => {
        const { getCookie } = await import('@tanstack/react-start/server')
        const token = getCookie('accessToken')

        if (!token) {
          return {}
        }

        return {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      },
      onResponse: async response => {
        if (response.status !== 401) return
        throw redirect({ to: '/signout' })
      },
    })
  })
  .client(() => {
    return treaty<App>(window.location.origin, {
      fetch: {
        cache: 'no-store',
      },
      parseDate: false,
      onRequest: async () => {
        const token = getClientCookie('accessToken')

        if (!token || typeof token !== 'string') {
          return {}
        }

        return {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      },
    })
  })

export async function ensureApiResponse<
  TResponse extends MaybeFunction<MaybePromise<Treaty.TreatyResponse<{}>>>,
  TData extends Treaty.Data<TResponse> = NonNullable<
    Awaited<UnwrapMaybeFunction<TResponse>>['data']
  >,
>(data: TResponse): Promise<TData> {
  const response = (await data) as unknown as NonNullable<
    Awaited<UnwrapMaybeFunction<TResponse>>
  >

  if (response.error) {
    const serializedError = JSON.parse(JSON.stringify(response.error))
    throw new ApiV2Error(serializedError)
  }

  return response.data as TData
}

export type ApiV2Client = ReturnType<typeof getApi>
